import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteConfirmModal from "./delete-confirm-modal";
import { adminCourseDeleteAction, adminCoursesListAction } from "../Action/action";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function courseStatusLabel(c) {
  if (!c) return "—";
  if (c.active !== undefined && c.active !== null) return Number(c.active) ? "Active" : "Draft";
  return "—";
}

function htmlToText(html) {
  return String(html || "")
    .replace(/&nbsp;/g, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function truncateText(s, max = 110) {
  const v = String(s || "");
  if (v.length <= max) return v;
  return `${v.slice(0, max - 1)}…`;
}

export default function Courses() {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All");
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [courses, setCourses] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 10 });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [query, status]);

  const badgeFor = (s) => {
    if (s === "Active") return "badge-success";
    if (s === "Draft") return "badge-gray";
    if (s === "Coming Soon") return "badge-warning";
    if (s === "Archived") return "badge-gray";
    return "badge-info";
  };

  useEffect(() => {
    let cancelled = false;
    const statusParam =
      status === "Active" ? "active" : status === "Draft" ? "draft" : status === "Archived" ? "draft" : status === "Coming Soon" ? "draft" : undefined;
    const searchParam = query.trim() ? query.trim() : undefined;

    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminCoursesListAction({
          page,
          per_page: perPage,
          q: searchParam,
          status: statusParam
        });

        if (cancelled) return;
        if (!res?.success) {
          setCourses([]);
          setMeta({ total: 0, page, per_page: perPage });
          setError(res?.message || res?.msg || "Failed to load courses.");
          return;
        }

        setCourses(Array.isArray(res.data) ? res.data : []);
        setMeta({
          total: Number(res?.meta?.total || 0),
          page: Number(res?.meta?.page || page),
          per_page: Number(res?.meta?.per_page || perPage)
        });
      } catch (e) {
        if (cancelled) return;
        setCourses([]);
        setMeta({ total: 0, page, per_page: perPage });
        setError(e?.response?.data?.message || e?.message || "Failed to load courses.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [page, perPage, query, status, reloadNonce]);

  const rows = useMemo(() => {
    return courses.map((c) => {
      const title = c?.title || "—";
      const shortDescriptionRaw = htmlToText(c?.short_description);
      const shortDescription = shortDescriptionRaw ? truncateText(shortDescriptionRaw, 120) : "—";
      const ageGroup = c?.age_group || "—";
      const price = c?.price !== undefined && c?.price !== null ? String(c.price) : "—";
      const weeks = c?.weeks_count !== undefined && c?.weeks_count !== null ? Number(c.weeks_count) : "—";
      const created = formatDate(c?.created_at);
      const statusLabel = courseStatusLabel(c);
      return {
        raw: c,
        id: c?.id,
        title,
        shortDescription,
        ageGroup,
        price,
        weeks,
        created,
        statusLabel
      };
    });
  }, [courses]);

  const total = meta?.total ? Number(meta.total) : rows.length;
  const totalPages = total ? Math.max(1, Math.ceil(total / perPage)) : 1;
  const start = total && rows.length ? (page - 1) * perPage + 1 : 0;
  const end = total && rows.length ? start + rows.length - 1 : 0;

  const pageButtons = useMemo(() => {
    const max = 5;
    const safeTotal = Math.max(1, totalPages);
    const current = Math.min(Math.max(1, page), safeTotal);
    let s = Math.max(1, current - 2);
    let e = Math.min(safeTotal, s + max - 1);
    s = Math.max(1, e - max + 1);
    const arr = [];
    for (let i = s; i <= e; i += 1) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const openDelete = (row) => {
    try {
      if (!row?.id) return;
      setDeleteRow(row);
      setDeleteOpen(true);
    } catch (e) {}
  };

  const confirmDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    try {
      const res = await adminCourseDeleteAction(deleteRow.id);
      if (!res?.success) throw new Error(res?.message || res?.msg || "Delete failed");
      toast.success("Course deleted", { toastId: "course-delete" });
      setDeleteOpen(false);
      setDeleteRow(null);
      setReloadNonce((n) => n + 1);
    } catch (e) {
      toast.error("Delete failed", { toastId: "course-delete" });
    } finally {
      setDeleting(false);
    }
  };

  const clearAndReload = () => {
    setQuery("");
    setStatus("All");
    setPage(1);
    setError("");
    setReloadNonce((n) => n + 1);
  };

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Courses data unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="toolbar">
        <div className="flex items-center gap-3">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search courses..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="filter-group">
            {["All", "Active", "Draft", "Coming Soon", "Archived"].map((v) => (
              <button
                key={v}
                className={`filter-btn ${status === v ? "active" : ""}`}
                type="button"
                onClick={() => setStatus(v)}
              >
                {v}
              </button>
            ))}
          </div>
          <button className="btn btn-outline" type="button" onClick={clearAndReload} disabled={loading}>
            ↻ Clear
          </button>
        </div>
        <Link to="new" className="btn btn-primary">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </svg>
          Add New Course
        </Link>
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Course</th>
                <th>Age Group</th>
                <th>Price (AUD)</th>
                <th>Weeks</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state" style={{ padding: "28px 12px" }}>
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : rows.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="course-icon blue">{String(c.title || "C").slice(0, 1).toUpperCase()}</div>
                      <div>
                        <strong>{c.title}</strong>
                        <div className="small-text">{c.shortDescription}</div>
                      </div>
                    </div>
                  </td>
                  <td>{c.ageGroup}</td>
                  <td>
                    <strong>{c.price !== "—" ? c.price : "—"}</strong>
                  </td>
                  <td>{c.weeks !== "—" ? `${c.weeks} weeks` : "—"}</td>
                  <td>
                    <span className={`badge ${badgeFor(c.statusLabel)}`}>{c.statusLabel}</span>
                  </td>
                  <td>{c.created}</td>
                  <td>
                    <div className="actions-cell">
                      <Link to={`${c.id}/edit`} className="action-link">
                        Edit
                      </Link>
                      <Link to={`${c.id}/steps`} className="action-link">
                        Steps
                      </Link>
                      <Link to={`${c.id}/weeks`} className="action-link text-accent">
                        Weeks
                      </Link>
                      <button
                        type="button"
                        className="action-link text-danger"
                        onClick={() => openDelete(c)}
                        style={{ background: "transparent", border: "none", padding: 0 }}
                      >
                        Delete
                      </button>
                  
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state">
                      <div className="empty-icon">🫥</div>
                      No courses match your filters.
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <span>
            Showing {start}-{end} of {total} Courses
          </span>
          <div className="pagination-pages">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page <= 1 || loading}
              title="First page"
              aria-label="First page"
            >
              {"<<"}
            </button>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
              ‹
            </button>
            {pageButtons.map((p) => (
              <button
                key={p}
                type="button"
                className={p === page ? "active" : ""}
                onClick={() => setPage(p)}
                disabled={loading}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
            >
              ›
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages || loading}
              title="Last page"
              aria-label="Last page"
            >
              {">>"}
            </button>
          </div>
        </div>
      </div>

      <DeleteConfirmModal
        open={deleteOpen}
        itemType="course"
        itemName={deleteRow?.title || "Course"}
        onCancel={() => {
          if (deleting) return;
          setDeleteOpen(false);
          setDeleteRow(null);
        }}
        onConfirm={confirmDelete}
        confirmDisabled={deleting}
      />
    </div>
  );
}

